import React, { useEffect, useState } from 'react';
import {
  decodeJwt,
  getAuthToken,
  getExpiryDate,
  redirectToIdentityProvider,
} from 'utils/auth.util';
import { hasPermissions } from 'utils/permissions.util';
import { useLocation } from 'react-router';
import OAuthTokenSetter from '../OAuthTokenSetter/OAuthTokenSetter';
import FullPageLoader from '../../Loader/FullPageLoader/FullPageLoader';
import { retrieveAndSetPermissions } from './retrieveAndSetPermissions';
import { addMinutes, isBefore } from 'date-fns';
import { RefreshAuthPrompt } from '../RefreshAuthPrompt/RefreshAuthPrompt';

interface AuthHandlerProps {
  children: React.ReactNode;
}

function AuthHandler({ children }: AuthHandlerProps) {
  const location = useLocation();
  const { hash } = location;

  const permissionsSet = hasPermissions();
  const [error, setError] = useState<Error>();
  const [, setDateToForceRerender] = useState<Date>(new Date());

  const forceRerender = () => setDateToForceRerender(new Date());

  const token = getAuthToken();
  const isRedirectFromIdentityProvider = !!(hash && hash.includes('id_token='));
  const haveTokenNeedPermissions = !!token && !permissionsSet;
  const haveTokenAndPermissions = !!token && permissionsSet;

  const resolvePermissions = () => {
    retrieveAndSetPermissions()
      .then(() => forceRerender())
      .catch(error => {
        setError(error);
      });
  };

  useEffect(redirectToIdentityProviderIfTokenIsOld, [location]);

  if (error) {
    throw new Error(error.message);
  }

  if (isRedirectFromIdentityProvider) {
    return <OAuthTokenSetter />;
  }

  if (!token) {
    redirectToIdentityProvider();
  }

  if (haveTokenNeedPermissions) {
    resolvePermissions();
  }

  if (haveTokenAndPermissions) {
    return (
      <>
        <RefreshAuthPrompt />
        {children}
      </>
    );
  }

  return <FullPageLoader />;
}

function redirectToIdentityProviderIfTokenIsOld() {
  const token = getAuthToken();
  if (token) {
    const fifteenMinutesFromNow = addMinutes(new Date(), 15);
    const tokenExpiry = getExpiryDate(decodeJwt(token));

    if (isBefore(tokenExpiry, fifteenMinutesFromNow)) {
      redirectToIdentityProvider();
    }
  }
}

export default AuthHandler;
